// Биллинг подписки Resstro для админки владельца.
// Переиспользуем те же эндпоинты, что и ЛК лендинга:
//   GET  /api/public/me        — текущая подписка + лимиты + платежи (owner JWT)
//   GET  /api/public/plans     — каталог тарифов для смены
//   POST /api/billing/checkout — создать платёж ЮKassa (owner JWT)
//   POST /api/billing/cancel   — отменить авто-продление
//   POST /api/billing/schedule/cancel — отменить запланированную смену тарифа
//
// ВАЖНО: единицы в ответах разные, не перепутать:
//   • цены тарифов (`PlanDetails.priceMonth/priceYear`, `PublicPlan.*`) — РУБЛИ → fmtRub
//   • сумма подписки и платежей (`SubscriptionInfo.amount`, `payments[].amount`) — КОПЕЙКИ → fmtKop
import { get, post } from './api';

export type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';
export type Period = 'MONTHLY' | 'YEARLY';

export interface PlanDetails {
  code: string;
  name: string;
  tagline: string | null;
  priceMonth: number;
  priceYear: number;
  features: string[];
  highlights: string[];
  maxEstablishments: number | null;
  maxCashiers: number | null;
  maxUsers: number | null;
}

export interface SubscriptionPayment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  method: string | null;
  createdAt: string;
  paidAt: string | null;
}

export interface SubscriptionInfo {
  status: SubscriptionStatus;
  plan: string;
  billingPeriod: Period;
  amount: number;
  currency: string;
  trialEndsAt: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string | null;
  cancelledAt: string | null;
  daysLeft: number | null;
  /** ok — срок идёт; grace — истёк, касса работает ещё graceDaysLeft суток; blocked — новые заказы и смены закрыты. */
  accessState: 'ok' | 'grace' | 'blocked';
  graceDaysLeft: number;
  scheduledChange: {
    planCode: string;
    planName: string;
    billingPeriod: Period;
    additional: number;
    applyAt: string;
  } | null;
  planDetails: PlanDetails | null;
  payments: SubscriptionPayment[];
}

export interface MeResponse {
  subscription: SubscriptionInfo | null;
  establishment: { id: string; name: string } | null;
}

export interface PublicPlan {
  code: string;
  name: string;
  tagline: string | null;
  description: string | null;
  priceMonth: number;
  priceYear: number;
  monthlySavingsPct: number;
  extraEstablishmentPriceMonth: number | null;
  maxEstablishments: number | null;
  maxCashiers: number | null;
  maxUsers: number | null;
  features: string[];
  highlights: string[];
  isPopular: boolean;
}

export interface CheckoutResponse {
  paymentId: string;
  confirmationUrl: string;
  amountKop: number;
}

export const getMe = () => get<MeResponse>('/api/public/me');

export const getPublicPlans = () => get<{ plans: PublicPlan[] }>('/api/public/plans');

export const createCheckout = (body: {
  planCode?: string;
  billingPeriod?: Period;
  additionalEstablishments?: number;
  returnUrl?: string;
}) => post<CheckoutResponse>('/api/billing/checkout', body);

export const cancelSubscription = (reason?: string) =>
  post<{ ok: true }>('/api/billing/cancel', reason ? { reason } : {});

export const cancelScheduledChange = () =>
  post<{ ok: true }>('/api/billing/schedule/cancel', {});

/** Рубли (НЕ копейки) → «2 490 ₽». Для цен тарифов из /api/public/plans. */
export function fmtRub(value: number): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(value);
}

/** Копейки → «2 500 ₽». Для `subscription.amount` и сумм платежей. */
export function fmtKop(kopecks: number): string {
  return fmtRub(Math.round(kopecks / 100));
}
